import { ensureValidToken } from '../auth/keycloak';
import { API_CONFIG } from '../config/env';
import { fetchWithRetry } from './retryFetch';
import { navigateEvent } from './navigation';
import i18n from '../plugins/i18n';
import { logger } from '../utils/logger';
import { initGlobalFetchErrorTelemetry, reportApiErrorTelemetry } from './clientTelemetry';
import { withSelectedAssignmentFolder } from '../utils/assignmentFolderSelection';

export const API_BASE_URL: string = API_CONFIG.baseUrl;
initGlobalFetchErrorTelemetry();

// Track server status for maintenance notification
let serverWasDown = false;

function createClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function checkServerError(error: unknown): boolean {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network error (server unreachable)
    return true;
  }
  return false;
}

type ApiErrorBody = {
  error?: string;
  message?: string;
  [key: string]: unknown;
};

async function readErrorBody(res: Response): Promise<ApiErrorBody> {
  const text = await res.text();
  if (!text) {
    return {};
  }

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return parsed as ApiErrorBody;
    }
  } catch {
    // Fall through to raw text below.
  }

  return { message: text };
}

function handleServerStatus(res: Response | null, error?: unknown) {
  // Check if server is down (502, 503, or network error)
  const isServerDown = 
    (res && (res.status === 502 || res.status === 503)) ||
    (error && checkServerError(error));

  if (isServerDown && !serverWasDown) {
    // Server just went down
    serverWasDown = true;
    window.dispatchEvent(new Event('server-maintenance'));
  } else if (!isServerDown && serverWasDown) {
    // Server recovered
    serverWasDown = false;
    window.dispatchEvent(new Event('server-recovered'));
  }
}

async function withAuth(init: RequestInit = {}): Promise<RequestInit> {
  try {
    const token = await ensureValidToken(30);
    if (!token) throw new Error('Authentication required');
    const headers = new Headers(init.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }
    if (!headers.has('X-Client-Request-Id')) {
      headers.set('X-Client-Request-Id', createClientRequestId());
    }
    return { ...init, headers };
  } catch (error) {
    logger.error('Failed to get valid token:', error);
    throw Object.assign(new Error('Authentication required'), { status: 401, cause: error });
  }
}

async function handleForbiddenResponse(res: Response) {
  if (res.status === 403) {
    try {
      const data = await res.clone().json();
      if (data.error === 'USER_BANNED') {
        // Use React Router navigation instead of hard redirect
        navigateEvent('/forbidden', { 
          replace: true, 
          state: { banReason: data.banReason || i18n.t('error.noBanReasonProvided') } 
        });
        throw new Error('USER_BANNED');
      }
    } catch (e) {
      // If not banned error, continue with normal error handling
    }
  }
}

// Common fetch function to eliminate code duplication
async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    const authInit = await withAuth(init);
    const res = await fetchWithRetry(`${API_CONFIG.baseUrl}${path}`, authInit);
    handleServerStatus(res);
    await handleForbiddenResponse(res);
    if (!res.ok) {
      const errorBody = await readErrorBody(res);
      const fallbackMessage = `[API] ${authInit.method || 'GET'} ${path} failed: ${res.status}`;
      const errorMessage = typeof errorBody.message === 'string' && errorBody.message.trim()
        ? errorBody.message
        : fallbackMessage;

      throw Object.assign(new Error(errorMessage), errorBody, {
        status: res.status,
        response: errorBody,
        rawMessage: errorMessage,
      });
    }
    if (res.status === 204 || res.headers.get('content-length') === '0') {
      return {} as T;
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : {}) as T;
  } catch (error) {
    handleServerStatus(null, error);
    throw error;
  }
}

export function apiGet<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, { ...init, method: 'GET', credentials: 'include' });
}

export function apiPost<T>(path: string, body: unknown, init: RequestInit = {}): Promise<T> {
  let normalizedBody = body === undefined ? {} : body;
  const isClassroomAssignmentCreate = /^\/api\/teacher\/classrooms\/[^/]+\/assignments$/.test(path);
  if (
    isClassroomAssignmentCreate
    && typeof window !== 'undefined'
    && normalizedBody !== null
    && typeof normalizedBody === 'object'
    && !Array.isArray(normalizedBody)
  ) {
    normalizedBody = withSelectedAssignmentFolder(
      normalizedBody as Record<string, unknown>,
      window.location.search,
    );
  }
  if (body === undefined) {
    const stack = new Error('apiPost undefined body').stack;
    logger.error(`[API] POST ${path} called with undefined body, defaulting to empty object`, {
      stack,
      method: 'POST',
      path,
    });
  }

  return apiFetch<T>(path, {
    ...init,
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...init.headers },
    body: JSON.stringify(normalizedBody),
  });
}

export function apiPut<T>(path: string, body: unknown, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, {
    ...init,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...init.headers },
    body: JSON.stringify(body),
  });
}

export function apiDelete<T>(path: string, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, { ...init, method: 'DELETE' });
}

export function apiPatch<T>(path: string, body: unknown, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, {
    ...init,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...init.headers },
    body: JSON.stringify(body),
  });
}

// Special function for file upload (FormData)
export function apiPostFormData<T>(path: string, formData: FormData, init: RequestInit = {}): Promise<T> {
  return apiFetch<T>(path, {
    ...init,
    method: 'POST',
    body: formData,
    // Don't set Content-Type for FormData - browser will set it with boundary
  });
}

// Fetch binary payloads (Blob) with auth, for preview/download
export async function apiGetBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  try {
    const authInit = await withAuth({ 
      ...init, 
      method: 'GET', 
      credentials: 'include', 
      headers: { 
        ...(init.headers || {}), 
        Accept: '*/*',
        'X-Requested-With': 'XMLHttpRequest'  // Prevent IDM from intercepting
      } 
    });
    const res = await fetchWithRetry(`${API_CONFIG.baseUrl}${path}`, authInit);
    handleServerStatus(res);
    await handleForbiddenResponse(res);
    if (!res.ok) {
      throw new Error(`[API] GET ${path} failed: ${res.status}`);
    }
    return await res.blob();
  } catch (error) {
    handleServerStatus(null, error);
    throw error;
  }
}

export async function apiPostBlob(path: string, body: unknown, init: RequestInit = {}): Promise<Blob> {
  try {
    const authInit = await withAuth({
      ...init,
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
        ...(init.headers || {}),
      },
      body: JSON.stringify(body ?? {}),
    });
    const res = await fetchWithRetry(`${API_CONFIG.baseUrl}${path}`, authInit);
    handleServerStatus(res);
    await handleForbiddenResponse(res);
    if (!res.ok) {
      const errorBody = await readErrorBody(res);
      throw Object.assign(new Error(
        typeof errorBody.message === 'string' && errorBody.message.trim()
          ? errorBody.message
          : `[API] POST ${path} failed: ${res.status}`
      ), errorBody, { status: res.status });
    }
    return await res.blob();
  } catch (error) {
    handleServerStatus(null, error);
    throw error;
  }
}

/**
 * Fetch blob with progress callback - useful for large file downloads with progress bar
 * Uses XMLHttpRequest for progress events with proper auth headers
 */
export async function apiGetBlobWithProgress(
  path: string,
  onProgress?: (loaded: number, total: number) => void
): Promise<ArrayBuffer> {
  const authInit = await withAuth({ headers: { Accept: '*/*', 'X-Requested-With': 'XMLHttpRequest' } });
  const baseUrl = API_CONFIG.baseUrl;
  
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${baseUrl}${path}`, true);
    xhr.responseType = 'arraybuffer';
    
    // Set auth headers
    new Headers(authInit.headers).forEach((value, name) => xhr.setRequestHeader(name, value));
    xhr.withCredentials = true;
    
    // Progress handler
    xhr.onprogress = (event) => {
      if (onProgress) {
        onProgress(event.loaded, event.lengthComputable ? event.total : 0);
      }
    };
    
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response as ArrayBuffer);
      } else {
        reportApiErrorTelemetry({
          path,
          baseUrl,
          method: 'GET',
          requestInit: {
            method: 'GET',
            headers: {
              Authorization: '[REDACTED]',
              'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'include',
          },
          status: xhr.status,
          error: new Error(`HTTP ${xhr.status}: ${xhr.statusText}`),
        });
        reject(Object.assign(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`), { status: xhr.status }));
      }
    };
    
    xhr.onerror = () => {
      reportApiErrorTelemetry({
        path,
        baseUrl,
        method: 'GET',
        requestInit: {
          method: 'GET',
          headers: {
            Authorization: '[REDACTED]',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'include',
        },
        error: new Error('Network error'),
      });
      reject(new Error('Network error'));
    };
    
    xhr.send();
  });
}

export type MeClaims = { id: number; role: 'ADMIN' | 'TEACHER' | 'STUDENT'; email: string };
export async function apiMeClaims(): Promise<MeClaims> {
  return apiGet<MeClaims>('/api/me/claims');
}

/**
 * Open a streaming response (SSE) with auth/error handling from this module.
 * Returns the raw Response so the caller owns the reader/buffer; no JSON parse
 * or buffering is applied. Reuses withAuth so the bearer token is attached from
 * the Authorization header (never a URL param).
 */
export async function apiOpenStream(path: string, signal?: AbortSignal): Promise<Response> {
  const authInit = await withAuth({ signal, headers: { Accept: 'text/event-stream' } });
  const res = await fetch(`${API_CONFIG.baseUrl}${path}`, authInit);
  handleServerStatus(res);
  await handleForbiddenResponse(res);
  if (!res.ok) {
    const errorBody = await readErrorBody(res);
    throw Object.assign(new Error(errorBody.message || `[API] stream failed: ${res.status}`), errorBody, {
      status: res.status,
      response: errorBody,
    });
  }
  return res;
}
